<template>
  <v-container fluid class="pip-review pa-4" max-width="1400">
    <!-- Header -->
    <div class="d-flex align-center mb-5">
      <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" :to="`/admin/analysis`" class="mr-3">
        Back
      </v-btn>
      <v-icon color="primary" size="26" class="mr-2">mdi-dice-multiple</v-icon>
      <div>
        <h1 class="text-h6 font-weight-bold mb-0">Pip Review</h1>
        <p class="text-caption text-medium-emphasis mb-0">
          Job: {{ jobId }} — {{ reviewed }}/{{ total }} reviewed
        </p>
      </div>
      <v-spacer />
      <div class="d-flex align-center gap-3">
        <v-btn-toggle v-model="filter" density="compact" variant="outlined" rounded="lg">
          <v-btn value="all"        size="small">All</v-btn>
          <v-btn value="unreviewed" size="small" color="warning">Unreviewed</v-btn>
          <v-btn value="reviewed"   size="small" color="success">Reviewed</v-btn>
        </v-btn-toggle>
        <v-progress-linear
          :model-value="total ? (reviewed / total) * 100 : 0"
          color="success"
          rounded
          height="8"
          style="width: 120px"
        />
        <span class="text-caption">{{ total ? Math.round((reviewed / total) * 100) : 0 }}%</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
      <p class="text-caption mt-3">Loading crops...</p>
    </div>

    <!-- Empty -->
    <div v-else-if="!filteredCrops.length" class="text-center py-12 text-disabled">
      <v-icon size="48" class="mb-2">mdi-image-off-outline</v-icon>
      <p>{{ filter === 'unreviewed' ? 'All crops reviewed!' : 'No crops found for this job.' }}</p>
    </div>

    <!-- Crop grid -->
    <div v-else class="crop-grid">
      <div
        v-for="crop in filteredCrops"
        :key="crop.crop_file"
        class="crop-card"
        :class="{
          'is-reviewed': crop.reviewed,
          'is-active':   activeCrop?.crop_file === crop.crop_file,
        }"
        @click="selectCrop(crop)"
      >
        <div class="crop-image-wrapper">
          <img
            :src="cropImageUrl(crop.crop_file)"
            :alt="`Die crop ${crop.crop_file}`"
            class="crop-image"
            loading="lazy"
          />
          <div v-if="crop.pip_value" class="pip-badge">{{ crop.pip_value }}</div>
          <div v-if="crop.reviewed" class="reviewed-overlay">
            <v-icon size="20" color="white">mdi-check</v-icon>
          </div>
        </div>
        <div class="crop-meta">
          <span class="text-caption text-medium-emphasis">{{ formatTime(crop.timestamp_sec) }}</span>
          <span class="text-caption text-medium-emphasis">conf: {{ (crop.confidence * 100).toFixed(0) }}%</span>
        </div>
      </div>
    </div>

    <!-- Pip value selector (bottom bar) -->
    <Teleport to="body">
      <div v-if="activeCrop" class="pip-selector-bar">
        <div class="pip-selector-inner">
          <div class="d-flex align-center gap-3 mr-4">
            <img
              :src="cropImageUrl(activeCrop.crop_file)"
              class="active-crop-preview"
              alt="active crop"
            />
            <div>
              <div class="text-caption text-medium-emphasis">Select pip value</div>
              <div class="text-caption">{{ formatTime(activeCrop.timestamp_sec) }}</div>
              <div class="text-caption text-disabled">Keys: 1-6 · N=skip · Esc=close</div>
            </div>
          </div>

          <div class="pip-buttons">
            <button
              v-for="n in 6"
              :key="n"
              class="pip-btn"
              :class="{ 'pip-btn--active': activeCrop.pip_value === n }"
              @click="savePip(activeCrop, n)"
            >
              <DiceFace :value="n" :size="28" />
              <span>{{ n }}</span>
            </button>
          </div>

          <div class="d-flex gap-2 ml-4">
            <v-btn size="small" variant="outlined" color="error" @click="savePip(activeCrop, null)">
              Skip
            </v-btn>
            <v-btn size="small" variant="text" @click="activeCrop = null">
              Close
            </v-btn>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Click hint -->
    <div v-if="!activeCrop && filteredCrops.length" class="text-center text-disabled mt-4">
      <v-icon size="16" class="mr-1">mdi-cursor-default-click</v-icon>
      <span class="text-caption">Click a crop to assign pip value</span>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '../../services/api'
import DiceFace from '../../components/DiceFace.vue'

const route  = useRoute()
const jobId  = route.params.jobId || route.query.jobId

const apiBase     = api.defaults.baseURL || ''
const cropDirName = ref('')

const loading    = ref(true)
const crops      = ref([])
const activeCrop = ref(null)
const filter     = ref('unreviewed')

const total    = computed(() => crops.value.length)
const reviewed = computed(() => crops.value.filter(c => c.reviewed).length)

const filteredCrops = computed(() => {
  if (filter.value === 'unreviewed') return crops.value.filter(c => !c.reviewed)
  if (filter.value === 'reviewed')   return crops.value.filter(c => c.reviewed)
  return crops.value
})

function cropImageUrl(cropFile) {
  if (!cropDirName.value || !cropFile) return ''
  return `${apiBase}/training/videos/analyze/crop-image?jobId=${encodeURIComponent(cropDirName.value)}&file=${encodeURIComponent(cropFile)}`
}

async function loadCrops() {
  loading.value = true
  try {
    const { data } = await api.get(`/training/videos/analyze/crops?jobId=${jobId}`)
    crops.value       = data.crops || []
    cropDirName.value = data.cropDirName || jobId
  } catch (e) {
    console.error('Failed to load crops', e)
  } finally {
    loading.value = false
  }
}

function selectCrop(crop) {
  activeCrop.value = crop
}

async function savePip(crop, pipValue) {
  try {
    await api.put(`/training/videos/analyze/crops?jobId=${jobId}`, {
      cropFile: crop.crop_file,
      pipValue,
    })
    const local = crops.value.find(c => c.crop_file === crop.crop_file)
    if (local) { local.pip_value = pipValue; local.reviewed = true }

    // Advance to next unreviewed
    const unreviewed = crops.value.filter(c => !c.reviewed)
    activeCrop.value = unreviewed.length ? unreviewed[0] : null
  } catch (e) {
    console.error('Failed to save pip value', e)
  }
}

function formatTime(sec) {
  if (sec == null) return '—'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function onKeyDown(e) {
  if (!activeCrop.value) return
  const n = parseInt(e.key)
  if (n >= 1 && n <= 6) { savePip(activeCrop.value, n); return }
  if (e.key === 'n' || e.key === 'N') { savePip(activeCrop.value, null); return }
  if (e.key === 'Escape') { activeCrop.value = null }
}

onMounted(() => {
  loadCrops()
  window.addEventListener('keydown', onKeyDown)
})
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))
</script>

<style scoped>
.pip-review {
  min-height: calc(100vh - 64px);
  padding-bottom: 180px; /* space for bottom bar */
}

.crop-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.crop-card {
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.15s, transform 0.1s;
  background: rgba(var(--v-theme-surface-variant), 0.4);
}
.crop-card:hover     { transform: scale(1.04); border-color: rgba(var(--v-theme-primary), 0.5); }
.crop-card.is-active { border-color: rgb(var(--v-theme-primary)); }
.crop-card.is-reviewed { opacity: 0.55; }

.crop-image-wrapper {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
}
.crop-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: pixelated;
}

.pip-badge {
  position: absolute; top: 3px; right: 3px;
  background: rgb(var(--v-theme-primary));
  color: white;
  font-size: 0.75rem; font-weight: 700;
  width: 20px; height: 20px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}

.reviewed-overlay {
  position: absolute; bottom: 3px; right: 3px;
  background: rgba(22, 163, 74, 0.85);
  border-radius: 50%;
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
}

.crop-meta {
  display: flex;
  justify-content: space-between;
  padding: 3px 5px;
}

/* Bottom pip selector — teleported to body so nothing clips it */
.pip-selector-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  background: rgba(20, 20, 30, 0.97);
  backdrop-filter: blur(16px);
  border-top: 1px solid rgba(255,255,255,0.1);
  padding: 14px 24px 20px 24px;
  z-index: 9998;
}

.pip-selector-inner {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
  gap: 8px;
}

.active-crop-preview {
  width: 72px; height: 72px;
  border-radius: 8px;
  object-fit: cover;
  image-rendering: pixelated;
  border: 2px solid rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

.pip-buttons {
  display: flex;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.pip-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 2px solid rgba(255,255,255,0.15);
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 700;
  transition: all 0.12s;
  color: rgba(255,255,255,0.8);
  min-width: 56px;
}
.pip-btn:hover {
  border-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  transform: translateY(-2px);
}
.pip-btn--active {
  background: rgb(var(--v-theme-primary));
  border-color: rgb(var(--v-theme-primary));
  color: white;
}
</style>